import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import compose from "ramda/src/compose";
import mergeRight from "ramda/src/mergeRight";
import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { useModalContext } from "../../contexts/ModalContext/context";
import { Grid } from "../../UI/Grid";
import { pathOrString } from "../../helpers/pathOrString";
import pathOr from "ramda/src/pathOr";
import { normalize } from "../../helpers/normalize";
import { getControllerTextFieldRender } from "../../helpers/getControllerTextFieldRender";
import { getControllerTextAreaRender } from "../../helpers/getControllerTextAreaRender";
import { RetainAnswerSelect } from "../RetainAnswerSelect";
import { useSnackbar } from "../../hooks/useSnackbar";
import { TagSelect } from "../TagSelect";
import { getTag } from "../../helpers/getTag";

const TITLES = {
  edit: "Modify Open Text Question",
  new: "Create Open Text Question",
};

export interface IProps {
  data: any;
  onHide: any;
  onSave: any;
  showTagColumn?: any;
}

interface RetainAnswer {
  value: boolean,
  label: string,
}

interface IFormData {
  questionText: string;
  placeholder: string;
  disclaimer: string;
  tag?: {
    value: number;
    label: string;
  };
  retainAnswer: boolean | null | RetainAnswer;
}

const getTagObj = (tag: any) =>
  tag?.value
    ? {
        tag: {
          id: tag.value,
          name: tag.label,
        },
      }
    : { tag: null, clearTag: true };

export const FreeTextQuestionModal: React.FC<IProps> = ({
  data,
  onHide,
  onSave,
  showTagColumn,
}) => {
  const { showErrorSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const { setIsOpen } = useModalContext();
  const {
    reset,
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IFormData>();

  const isEdit = typeof data === "object";
  const visible = isEdit || data === true;

  React.useEffect(() => {
    setIsOpen(visible);
  }, [visible]);

  useEffect(() => {
    setLoading(false);
  }, [data]);

  useEffect(() => {
    reset({
      questionText: pathOrString(["questionText"], data),
      placeholder: pathOrString(["placeholder"], data),
      disclaimer: pathOrString(["disclaimer"], data),
      tag: getTag(data) as any,
      retainAnswer: pathOr(true, ["retainAnswer"], data),
    });
  }, [normalize(data)]);

  const handleUpdate = async (newData: IFormData) => {
    setLoading(true);

    const getRetainAnswerValue = () => {
      if (newData.retainAnswer !== null && newData.retainAnswer !== undefined) {
        // Handle both boolean and object formats
        if (typeof newData.retainAnswer === 'boolean') {
          return newData.retainAnswer;
        }
        const retainAnswer = newData.retainAnswer as RetainAnswer;
        return retainAnswer.value;
      }
      return true;
    };


    const question = {
      ...(isEdit ? data : {}),
      ...getTagObj(newData.tag),
      questionType: "FREE_TEXT",
      questionText: newData.questionText,
      placeholder: newData.placeholder,
      placeholderText: newData.placeholder,
      disclaimer: newData.disclaimer,
      disclaimerText: newData.disclaimer,
      retainAnswer: getRetainAnswerValue(),
    };

    const { success, error } = await onSave(question);

    if (success) {
      onHide();
    } else {
      showErrorSnackbar(error);
    }
    setLoading(false);
  };

  const getDefaultInputProps = mergeRight({ errors });

  const getTextControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextFieldRender, getDefaultInputProps),
    []
  );

  const getTextAreaControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextAreaRender, getDefaultInputProps),
    []
  );

  return (
    <Modal
      maxWidth="md"
      onClose={onHide}
      title={isEdit ? TITLES.edit : TITLES.new}
      containerSx={{
        overflow: "visible",
      }}
      contentSx={{
        overflow: "visible",
      }}
      actions={
        <>
          <Button appearance="subtle" onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleUpdate)}
            isLoading={isLoading}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} width={658}>
        <Grid item xs={12}>
          <Controller
            name="questionText"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "Question",
              maxLength: 1000,
              required: true,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="placeholder"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "Placeholder",
              required: true,
              maxLength: 500,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="disclaimer"
            control={control}
            // @ts-ignore
            render={getTextAreaControllerRender({
              label: "Disclaimer (optional)",
              maxLength: 1000,
              minimumRows: 5,
            })}
          />
        </Grid>
        {showTagColumn && (
          <Grid item xs={12}>
            <Controller
              name="tag"
              control={control}
              render={({ field }: any) => (
                <TagSelect {...field} label="Tag (optional)" />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name="retainAnswer"
            control={control}
            render={({ field }: any) => (
              <RetainAnswerSelect {...field} label="Retain Answer" />
            )}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};
